import { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

const VOICED_PITCH_MIN_HZ = 1;

function findNearestIndex(xs, target) {
  if (!xs?.length || !Number.isFinite(Number(target))) return -1;

  let low = 0;
  let high = xs.length - 1;
  const value = Number(target);

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (Number(xs[mid]) < value) low = mid + 1;
    else high = mid;
  }

  if (low === 0) return 0;

  const previous = low - 1;
  return Math.abs(Number(xs[low]) - value) <
    Math.abs(Number(xs[previous]) - value)
    ? low
    : previous;
}

function buildContinuousGuideCurve(xs, ys) {
  if (!xs?.length || !ys?.length) return [];

  const out = new Array(ys.length).fill(null);

  for (let i = 0; i < ys.length; i++) {
    const y = ys[i];
    out[i] = y != null && Number.isFinite(Number(y)) ? Number(y) : null;
  }

  let firstValid = -1;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) {
      firstValid = i;
      break;
    }
  }
  if (firstValid === -1) return [];

  for (let i = 0; i < firstValid; i++) out[i] = out[firstValid];

  let lastValid = -1;
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] != null) {
      lastValid = i;
      break;
    }
  }
  for (let i = lastValid + 1; i < out.length; i++) out[i] = out[lastValid];

  let i = firstValid;
  while (i < out.length) {
    if (out[i] != null) {
      i++;
      continue;
    }

    const start = i - 1;
    let end = i;
    while (end < out.length && out[end] == null) end++;
    if (end >= out.length) break;

    const x1 = Number(xs[start]);
    const x2 = Number(xs[end]);
    const y1 = out[start];
    const y2 = out[end];
    const span = x2 - x1 || 1;

    for (let k = i; k < end; k++) {
      const x = Number(xs[k]);
      const t = (x - x1) / span;
      out[k] = y1 + (y2 - y1) * t;
    }

    i = end;
  }

  const smoothPass = (arr) => {
    const next = [...arr];
    for (let j = 1; j < arr.length - 1; j++) {
      next[j] = (arr[j - 1] + arr[j] + arr[j + 1]) / 3;
    }
    return next;
  };

  let smoothed = out.map((v) => Number(v));
  smoothed = smoothPass(smoothed);
  smoothed = smoothPass(smoothed);

  return xs.map((x, idx) => ({
    x: Number(x),
    y: smoothed[idx],
  }));
}

function interpolateOnCurve(points, xTarget) {
  if (!points?.length || !Number.isFinite(Number(xTarget))) return null;
  if (points.length === 1) return { x: xTarget, y: points[0].y };

  if (xTarget <= points[0].x) return { x: xTarget, y: points[0].y };
  if (xTarget >= points[points.length - 1].x) {
    return { x: xTarget, y: points[points.length - 1].y };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);

    if (xTarget >= minX && xTarget <= maxX) {
      const span = p2.x - p1.x || 1;
      const t = (xTarget - p1.x) / span;
      return {
        x: xTarget,
        y: p1.y + (p2.y - p1.y) * t,
      };
    }
  }

  return { x: xTarget, y: points[points.length - 1].y };
}

function makeCursorBallPlugin() {
  return {
    id: "cursorBallPlugin",
    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        cursorX = null,
        cursorEnvelopePoint = null,
        ballRadius = 12,
        showBall = true,
        isMobile = false,
      } = pluginOptions || {};

      if (!showBall || cursorX == null) return;

      const { ctx, chartArea, scales } = chart;
      const xScale = scales?.x;
      const yScale = scales?.y;
      if (!ctx || !chartArea || !xScale || !yScale) return;

      const { left, right, top, bottom } = chartArea;

      const xC = xScale.getPixelForValue(cursorX);
      if (Number.isFinite(xC) && xC >= left && xC <= right) {
        ctx.save();

        ctx.strokeStyle = "rgba(255,255,255,0.90)";
        ctx.lineWidth = isMobile ? 3 : 3.5;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.strokeStyle = "rgba(16,185,129,1)";
        ctx.lineWidth = isMobile ? 1.6 : 1.8;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.restore();
      }

      if (
        !cursorEnvelopePoint ||
        !Number.isFinite(Number(cursorEnvelopePoint.x)) ||
        !Number.isFinite(Number(cursorEnvelopePoint.y))
      ) {
        return;
      }

      const r = ballRadius ?? (isMobile ? 8.5 : 12);
      const px = xScale.getPixelForValue(cursorEnvelopePoint.x);
      const py = yScale.getPixelForValue(cursorEnvelopePoint.y);

      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      if (px < left || px > right || py < top || py > bottom) return;

      ctx.save();

      ctx.shadowColor = "rgba(16,185,129,0.48)";
      ctx.shadowBlur = isMobile ? 14 : 18;
      ctx.beginPath();
      ctx.arc(px, py, r + 1.25, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(16,185,129,0.95)";
      ctx.fill();

      ctx.shadowBlur = 0;

      const bodyGrad = ctx.createRadialGradient(
        px - r * 0.32,
        py - r * 0.34,
        r * 0.18,
        px,
        py,
        r
      );
      bodyGrad.addColorStop(0, "rgba(220,252,231,1)");
      bodyGrad.addColorStop(0.2, "rgba(134,239,172,1)");
      bodyGrad.addColorStop(0.5, "rgba(34,197,94,1)");
      bodyGrad.addColorStop(0.8, "rgba(16,185,129,1)");
      bodyGrad.addColorStop(1, "rgba(4,120,87,1)");

      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = bodyGrad;
      ctx.fill();

      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.34,
        py - r * 0.38,
        Math.max(1.8, r * 0.26),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.88)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.08,
        py - r * 0.14,
        Math.max(1.2, r * 0.11),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px + r * 0.28,
        py + r * 0.34,
        Math.max(1.4, r * 0.18),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(0,0,0,0.10)";
      ctx.fill();

      ctx.restore();
    },
  };
}

export default function IntensityPlotAudio({
  intensity,
  audioRef,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [animatedTime, setAnimatedTime] = useState(0);

  const rafRef = useRef(null);
  const displayTimeRef = useRef(0);

  const xs = useMemo(() => intensity?.intensity?.xs || [], [intensity]);
  const ysRaw = useMemo(() => intensity?.intensity?.ys || [], [intensity]);
  const pitchXs = useMemo(() => intensity?.pitch?.xs || [], [intensity]);
  const pitchYsRaw = useMemo(() => intensity?.pitch?.ys || [], [intensity]);
  const hasData = xs.length > 0 && ysRaw.length > 0;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const audio = audioRef?.current;

    if (!audio) {
      displayTimeRef.current = 0;
      setAnimatedTime(0);
      return undefined;
    }

    const stop = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const syncFromAudio = () => {
      const target = Number.isFinite(audio.currentTime)
        ? Math.max(0, audio.currentTime)
        : 0;

      const previous = displayTimeRef.current;
      displayTimeRef.current = target;

      if (previous == null || Math.abs(target - previous) > 0.0001) {
        setAnimatedTime(target);
      }
    };

    const tick = () => {
      syncFromAudio();
      rafRef.current = requestAnimationFrame(tick);
    };

    const syncEvents = [
      "seeked",
      "loadedmetadata",
      "ratechange",
      "play",
      "playing",
    ];

    syncEvents.forEach((eventName) => {
      audio.addEventListener(eventName, syncFromAudio);
    });

    syncFromAudio();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      stop();
      syncEvents.forEach((eventName) => {
        audio.removeEventListener(eventName, syncFromAudio);
      });
    };
  }, [audioRef]);

  const ys = useMemo(() => {
    return ysRaw.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });
  }, [ysRaw]);

  const pitchVoicedYs = useMemo(() => {
    return pitchYsRaw.map((value) => {
      const pitchValue = Number(value);
      return Number.isFinite(pitchValue) && pitchValue > VOICED_PITCH_MIN_HZ
        ? pitchValue
        : null;
    });
  }, [pitchYsRaw]);

  const validPoints = useMemo(() => {
    return xs
      .map((x, i) => ({ x: Number(x), y: ys[i] }))
      .filter((p) => Number.isFinite(p.x) && p.y != null);
  }, [xs, ys]);

  const { xMin, xMax } = useMemo(() => {
    if (pitchXs.length && pitchVoicedYs.length) {
      let firstVoiced = -1;
      let lastVoiced = -1;

      for (let i = 0; i < pitchVoicedYs.length; i++) {
        if (pitchVoicedYs[i] != null && Number.isFinite(Number(pitchXs[i]))) {
          firstVoiced = i;
          break;
        }
      }

      for (let i = pitchVoicedYs.length - 1; i >= 0; i--) {
        if (pitchVoicedYs[i] != null && Number.isFinite(Number(pitchXs[i]))) {
          lastVoiced = i;
          break;
        }
      }

      if (firstVoiced !== -1 && lastVoiced !== -1) {
        return {
          xMin: Number(pitchXs[firstVoiced]),
          xMax: Number(pitchXs[lastVoiced]),
        };
      }
    }

    if (!validPoints.length) return { xMin: 0, xMax: 1 };

    return {
      xMin: validPoints[0].x,
      xMax: validPoints[validPoints.length - 1].x,
    };
  }, [pitchXs, pitchVoicedYs, validPoints]);

  const { yMin, yMax } = useMemo(() => {
    if (!validPoints.length) return { yMin: 0, yMax: 1 };
    let min = Infinity;
    let max = -Infinity;
    for (const p of validPoints) {
      min = Math.min(min, p.y);
      max = Math.max(max, p.y);
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { yMin: 0, yMax: 1 };
    }
    return { yMin: min, yMax: max };
  }, [validPoints]);

  const yRange = useMemo(() => Math.max(1e-6, yMax - yMin), [yMax, yMin]);

  const cursorX = useMemo(() => {
    if (!xs.length) return null;

    const time = Number(animatedTime);
    if (!isFiniteNum(time) || time < xMin || time > xMax) return null;

    // Use the pitch contour as the voicing mask. The intensity graph remains
    // visible, but the cursor and ball disappear in silence and internal pauses.
    if (pitchXs.length && pitchVoicedYs.length) {
      const pitchIndex = findNearestIndex(pitchXs, time);
      if (pitchIndex < 0 || pitchVoicedYs[pitchIndex] == null) return null;
    }

    return time;
  }, [
    animatedTime,
    xs.length,
    xMin,
    xMax,
    pitchXs,
    pitchVoicedYs,
  ]);

  const showBall = cursorX != null;

  const guideCurve = useMemo(() => {
    return buildContinuousGuideCurve(xs, ys);
  }, [xs, ys]);

  const cursorEnvelopePoint = useMemo(() => {
    if (!guideCurve.length || cursorX == null) return null;

    const p = interpolateOnCurve(guideCurve, cursorX);
    if (!p) return null;

    return {
      x: p.x,
      y: p.y,
    };
  }, [guideCurve, cursorX]);

  const ballRadius = useMemo(() => {
    return isMobile ? 8.5 : 12;
  }, [isMobile]);

  const cursorBallPlugin = useMemo(() => makeCursorBallPlugin(), []);

  const data = useMemo(() => {
    if (!hasData) return { datasets: [] };

    return {
      datasets: [
        {
          label: "Intensity (dB)",
          data: xs.map((x, i) => ({ x, y: ys[i] })),
          borderColor: "#2563eb",
          borderWidth: isMobile ? 1.5 : 2,
          tension: isMobile ? 0.2 : 0.35,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          spanGaps: true,
          datalabels: { display: false },
        },
      ],
    };
  }, [hasData, xs, ys, isMobile]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      elements: {
        point: { radius: 0, hoverRadius: 0, hitRadius: 0 },
      },
      scales: {
        x: {
          type: "linear",
          min: xMin,
          max: xMax,
          grid: { display: true, color: "rgba(0,0,0,0.05)", drawBorder: false },
          ticks: { display: false },
          title: { display: false },
        },
        y: {
          min: yMin - yRange * 0.15,
          max: yMax + yRange * 0.15,
          grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
          title: {
            display: true,
            text: "Intensity (dB)",
            font: { size: isMobile ? 12 : 14 },
          },
          ticks: {
            font: { size: isMobile ? 10 : 12 },
            maxTicksLimit: 5,
            callback: (v) => Number(v).toFixed(1),
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
        },
        datalabels: { display: false },
        cursorBallPlugin: {
          cursorX,
          cursorEnvelopePoint,
          ballRadius,
          showBall,
          isMobile,
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
    }),
    [
      isMobile,
      xMin,
      xMax,
      yMin,
      yMax,
      yRange,
      cursorX,
      cursorEnvelopePoint,
      ballRadius,
      showBall,
    ]
  );

  if (!hasData) return null;

  return (
    <div className="w-full min-w-0">
      <div
        className={`w-full bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
      >
        <Line data={data} options={options} plugins={[cursorBallPlugin]} />
      </div>
    </div>
  );
}